import { Fragment } from "react";
import type { ReactNode } from "react";

import { useLocalization } from "@fluent/react";
import {
  Button,
  Chip,
  CollapsibleCard,
  ResponsiveFlex,
  Row,
} from "@narmi/design_system";
import {
  RECIPIENT_DRAWER_FLOW_CONFIG,
  hasMethod,
  isMethodOptional,
} from "byzantine/src/dbbl/businessLogic/modules/recipientDrawer";

import SectionHeader from "../../common/SectionHeader";

import styles from "./PaymentMethodsSection.module.scss";

import type {
  RecipientDrawerFlow,
  RecipientPaymentMethod,
} from "byzantine/src/dbbl/businessLogic/modules/recipientDrawer";

const METHOD_TITLE_IDS: Record<RecipientPaymentMethod, string> = {
  ach: "recipient-drawer-section-ach",
  wire: "recipient-drawer-section-domestic-wire",
  internationalWire: "recipient-drawer-section-international-wire",
};

const METHOD_ICONS: Record<RecipientPaymentMethod, string> = {
  ach: "narmi-icon-bank",
  wire: "narmi-icon-wires",
  internationalWire: "narmi-icon-wires",
};

interface PaymentMethodsSectionProps {
  // create numbers the sections
  stepNumber?: number;
  flow: RecipientDrawerFlow;
  recipient?: API.Recipient;
  methods: RecipientPaymentMethod[];
  // badged for review until the recipient has them
  methodsToReview: RecipientPaymentMethod[];
  isOpen: boolean;
  // create mode: nothing can be added until Profile is saved
  isLocked: boolean;
  openMethod: RecipientPaymentMethod | null;
  onToggle: () => void;
  onMethodOpen: (method: RecipientPaymentMethod) => void;
  // the open method's form
  renderForm: (method: RecipientPaymentMethod) => ReactNode;
}

const PaymentMethodsSection = ({
  stepNumber,
  flow,
  recipient,
  methods,
  methodsToReview,
  isOpen,
  isLocked,
  openMethod,
  onToggle,
  onMethodOpen,
  renderForm,
}: PaymentMethodsSectionProps) => {
  const { l10n } = useLocalization();
  const { requiredMethod } = RECIPIENT_DRAWER_FLOW_CONFIG[flow];

  return (
    <CollapsibleCard
      isOpen={isOpen}
      onOpen={onToggle}
      onClose={onToggle}
      isDisabled={isLocked}
      trigger="header"
      renderTitle={() => (
        <SectionHeader
          title={l10n.getString("recipient-drawer-payment-methods-title")}
          isOpen={isOpen}
          stepNumber={stepNumber}
        />
      )}
    >
      <ResponsiveFlex gapSize="m">
        <p className="fontColor--secondary fontSize--s">
          {l10n.getString("recipient-drawer-payment-methods-hint")}
        </p>
        {methods.map((method, index) => {
          const isAdded = Boolean(recipient && hasMethod(recipient, method));
          const isFormOpen = openMethod === method;
          const isDisabled = isLocked || (openMethod !== null && !isFormOpen);
          const titleClassName = isDisabled ? styles.disabled : undefined;
          return (
            <Fragment key={method}>
              <ResponsiveFlex gapSize="s">
                <Row alignItems="center" gapSize="xs">
                  <Row.Item shrink>
                    <span
                      className={`${METHOD_ICONS[method]} ${
                        styles.icon
                      } fontSize--l fontColor--theme--primary ${
                        titleClassName ?? ""
                      }`}
                    />
                  </Row.Item>
                  <Row.Item shrink>
                    <strong className={titleClassName}>
                      {l10n.getString(METHOD_TITLE_IDS[method])}
                    </strong>
                  </Row.Item>
                  {isAdded ? (
                    <Row.Item shrink>
                      <span
                        className={`narmi-icon-check fontColor--theme--secondary ${styles.icon}`}
                      />
                    </Row.Item>
                  ) : null}
                  {methodsToReview.includes(method) && !isAdded ? (
                    <Row.Item shrink>
                      <Chip
                        kind="warn"
                        label={l10n.getString(
                          "recipient-drawer-section-needs-review"
                        )}
                      />
                    </Row.Item>
                  ) : null}
                  {method === requiredMethod && !isAdded ? (
                    <Row.Item shrink>
                      <Chip
                        kind="warn"
                        label={l10n.getString("recipient-drawer-required-chip")}
                      />
                    </Row.Item>
                  ) : null}
                  {isMethodOptional(flow, method) && !isAdded ? (
                    <Row.Item shrink>
                      <span className="fontColor--secondary">
                        {l10n.getString("recipient-drawer-section-optional")}
                      </span>
                    </Row.Item>
                  ) : null}
                  <Row.Item />
                  {isFormOpen ? null : (
                    <Row.Item shrink>
                      <Button
                        kind="secondary"
                        size="s"
                        testId={`open-${method}-form`}
                        disabled={isDisabled}
                        onClick={() => onMethodOpen(method)}
                        label={l10n.getString(
                          isAdded
                            ? "button-edit"
                            : "recipient-drawer-add-method-button"
                        )}
                      />
                    </Row.Item>
                  )}
                </Row>
                {isFormOpen ? renderForm(method) : null}
              </ResponsiveFlex>
              {index < methods.length - 1 ? (
                <div className={styles.divider} />
              ) : null}
            </Fragment>
          );
        })}
      </ResponsiveFlex>
    </CollapsibleCard>
  );
};

export default PaymentMethodsSection;
